/**
 * Extract text from a PDF URL (same-origin, CORS-enabled, or blob URL).
 * Uses pdfjs-dist. For blob URLs we fetch as ArrayBuffer.
 */
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

const MAX_EXTRACTED_LENGTH = 80_000;

// Use unpkg worker so PDF parsing runs off the main thread (matches installed pdfjs-dist version)
const PDFJS_VERSION = '5.4.624';
if (typeof window !== 'undefined' && !GlobalWorkerOptions.workerSrc) {
  GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;
}

function isBlobUrl(url: string): boolean {
  return url.startsWith('blob:');
}

/** getTextContent() returns items: TextItem (has str) or TextMarkedContent (has type, id). */
function getTextFromItem(item: { str?: string } | { type: string; id?: string }): string {
  return typeof (item as { str?: string }).str === 'string' ? (item as { str: string }).str : '';
}

export async function extractTextFromPdf(pdfUrl: string): Promise<string> {
  let source: { url?: string; data?: ArrayBuffer };
  // Prefer fetching as ArrayBuffer so blob URLs and CORS-enabled URLs work; fallback to url for same-origin
  if (isBlobUrl(pdfUrl)) {
    const res = await fetch(pdfUrl);
    const data = await res.arrayBuffer();
    source = { data };
  } else {
    try {
      const res = await fetch(pdfUrl, { mode: 'cors' });
      if (res.ok) {
        const data = await res.arrayBuffer();
        source = { data };
      } else {
        source = { url: pdfUrl };
      }
    } catch {
      source = { url: pdfUrl };
    }
  }

  const loadingTask = getDocument(source);
  const pdf = await loadingTask.promise;
  const numPages = pdf.numPages;
  const parts: string[] = [];

  for (let i = 1; i <= numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = textContent.items.map(getTextFromItem).join(' ');
    if (pageText.trim()) parts.push(pageText);
  }

  let full = parts.join('\n\n');
  if (full.length > MAX_EXTRACTED_LENGTH) {
    full = full.slice(0, MAX_EXTRACTED_LENGTH) + '\n\n[... text truncated for length ...]';
  }
  return full.trim() || '';
}
